import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { submitCartAPI } from 'src/api';
import { ToastStyled } from 'src/components';
import { useCurrentUser } from 'src/hooks';
import type { CartGetData } from 'src/types';

export const useSubmitCart = () => {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cartId,
      layer,
      approverId,
    }: {
      cartId: string;
      layer: number;
      approverId?: string;
      approverEmail?: string;
      approverName?: string;
    }): Promise<boolean> => submitCartAPI(cartId, layer, approverId),
    onSuccess: (
      _,
      { cartId, layer, approverId, approverEmail, approverName },
    ) => {
      // toast.custom((toastBlock) => (
      //   <ToastStyled
      //     variant='success'
      //     handleClose={() => toast.dismiss(toastBlock)}
      //   >
      //     Cart {cartId} has been submitted for approval!
      //   </ToastStyled>
      // ));
      queryClient.invalidateQueries({
        queryKey: ['cart', 'list'],
        exact: false,
        type: 'all',
        refetchType: 'active',
      });

      try {
        queryClient.setQueryData(
          ['cart', cartId],
          (oldData: CartGetData): CartGetData => {
            // if multi-layer approval is enabled, there could be other details, so we need to add the new approval details to the existing array.
            const newApprovalDetails = oldData.approvalDetails || [];

            newApprovalDetails.push({
              requestedBy: {
                id: user?.key || '',
                email: user?.email || '',
                name: user?.name || '',
              },
              status: 'pending',
              requestedAt: Date.now(), // will differ slightly from what's in the DB but it won't matter for the UI.
              actionedAt: null,
              actionedBy: null,
              rejectNotes: '',
              requestedApprover: {
                id: approverId || '',
                email: approverEmail || '',
                name: approverName || '',
              },
              approvalLayer: layer,
            });

            return {
              ...oldData,
              approvalDetails: newApprovalDetails,
            };
          },
        );
      } catch (_) {
        queryClient.invalidateQueries({
          queryKey: ['cart', cartId],
          type: 'all',
        });
      }
    },
    onError: (_, { cartId }) => {
      toast.custom((toastBlock) => (
        <ToastStyled
          variant='error'
          handleClose={() => toast.dismiss(toastBlock)}
        >
          Failed to submit cart {cartId}. Please try again.
        </ToastStyled>
      ));
    },
  });
};
